import React, { useContext, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import {
  ConsolidatedCoinData,
  ConsolidatedCoinDataWithKey,
  LanguageNameSpaces,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { useIsSubUser, useSelectUser } from "@shared/utils/hooks/misc";
import { round } from "@shared/utils/numbers";
import useCoinCard from "@shared/features/portfolio/coins/useCoinCard";
import useGraphDataSocket from "@shared/services/socket/useGraphDataSocket";
import { DISPLAY_CURRENCIES, TIME_RANGE_1_DAY } from "common/constants";
import { portfolioFilters } from "@shared/slices/filters.slice";
import { EmptyStatesType } from "common/Icons/types";
import EmptyView from "common/Empty/EmptyView";
import FlatList from "common/Lists/FlatList";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_FIGURE_WEIGHT,
  newLookPhoneType,
} from "common/NewLook/tokens.ts";
import CoinActionsDropdown from "features/portfolio/common/CoinActionsDropdown";
import useAssetItem from "features/portfolio/common/useAssetItem";
import { useAssetsData } from "features/portfolio/common/hooks";
import NewLookSparkline from "features/portfolio/NewLook/NewLookSparkline.tsx";
import {
  PhoneActions,
  PhoneMeta,
  PhoneName,
  PhoneNames,
  PhoneRow,
  PhoneSub,
} from "features/portfolio/NewLook/PhoneRows.tsx";
import {
  getPhoneSegment,
  getTopShares,
  PhoneAllocationCard,
  toSplitSegments,
  phoneMoney,
  phonePercent,
  PhoneSegmentCard,
  PhoneSegmentRow,
  usePhoneRestColor,
} from "features/dashboard/PhoneDashboard/PhoneSummary";
import {
  PhoneSectionAction,
  PhoneSectionHead,
  PhoneSkeleton,
  PhoneSortMenu,
} from "features/dashboard/PhoneDashboard/PhoneParts";
import LiveSymbolPrice from "common/LiveComponents/LiveSymbolPrice.tsx";
import { PHONE_ASSET_ROW_ID, PHONE_ASSET_SPARK_ID } from "testIds";
import { RootState } from "app/store";
import { PrecisionConfig } from "types";
import AutoSizeText from "common/Text/AutoSizeText.tsx";

const INLINE_STYLE = { display: "inline" };
const FIGURE_MIN_FONT_SIZE = 10;
const SPARK_WIDTH = 46;

interface PhoneAssetsProps {
  dataSelector: (state: RootState) => ConsolidatedCoinData[];
  precisionSelector: (state: RootState) => PrecisionConfig | undefined;
  loading?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  /** Opens the buy modal (the inline entry: no FAB on the new-look phone). */
  onBuyClick?: () => void;
}

type SortKey = "value" | "name" | "change" | "price";
type ListItem = ConsolidatedCoinDataWithKey | { key: string; skeleton: true };

const isSkeleton = (item: ListItem): item is { key: string; skeleton: true } =>
  "skeleton" in item;

const getCoinValue = (coin: ConsolidatedCoinData, isBtc: boolean) =>
  (isBtc ? coin.total_in_btc : coin.total_in_usd) || 0;

/** The coin's 24h change as a fraction (display currency). */
const getCoinChange = (coin: ConsolidatedCoinData, isBtc: boolean) => {
  const change = coin.graph_data?.change as number[] | number | undefined;
  const value = Array.isArray(change) ? change[isBtc ? 1 : 0] : change;
  return typeof value === "number" ? value : undefined;
};

/**
 * Phone assets tab: what the coins are worth with an allocation ring of the
 * biggest holdings, three figure tiles, then the coins as rows of one grouped
 * card (quantity, value, 24h line and change).
 */
const PhoneAssets = ({
  dataSelector,
  precisionSelector,
  loading,
  refreshing,
  onRefresh,
  onBuyClick,
}: PhoneAssetsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const tokens = getNewLookTokens(useTheme());
  const restColor = usePhoneRestColor();
  const { search } = useContext(PageWrapperContext);
  const isBtc = useSelector(selectDisplayCurrency) === DISPLAY_CURRENCIES.BTC;
  const symbol = isBtc ? "₿" : "$";
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const [sortKey, setSortKey] = useState<SortKey>("value");

  const { filteredCoinsData: allCoins } = useAssetsData({ dataSelector });
  const { filteredCoinsData: found } = useAssetsData({
    dataSelector,
    searchFilter: search.value,
  });

  const summary = useMemo(() => {
    let total = 0;
    let available = 0;
    let dayChange = 0;
    allCoins.forEach((coin) => {
      const value = (isBtc ? coin.total_in_btc : coin.total_in_usd) || 0;
      total += value;
      available += (isBtc ? coin.available_in_btc : coin.available_in_usd) || 0;
      const change = getCoinChange(coin, isBtc);
      if (change !== undefined && change > -1)
        dayChange += value - value / (1 + change);
    });
    const shares = getTopShares(
      allCoins.map((coin) => ({
        key: String(coin.key),
        name: coin.symbol,
        group: coin.symbol,
        value: (isBtc ? coin.total_in_btc : coin.total_in_usd) || 0,
      })),
      4,
      t("phone.other"),
      restColor
    );
    const top = allCoins
      .slice()
      .sort((a, b) =>
        isBtc
          ? b.total_in_btc - a.total_in_btc
          : b.total_in_usd - a.total_in_usd
      )[0];
    return {
      total,
      available,
      dayChange,
      changePercent:
        total - dayChange ? (dayChange / (total - dayChange)) * 100 : 0,
      shares,
      top,
      topShare:
        top && total
          ? ((isBtc ? top.total_in_btc : top.total_in_usd) / total) * 100
          : 0,
    };
  }, [allCoins, isBtc, restColor, t]);

  const rows = useMemo(() => {
    const sorted = found.slice();
    if (sortKey === "name")
      sorted.sort((a, b) => a.symbol.localeCompare(b.symbol));
    else if (sortKey === "price")
      sorted.sort((a, b) => (b.btc_price || 0) - (a.btc_price || 0));
    else if (sortKey === "change")
      sorted.sort(
        (a, b) =>
          (getCoinChange(b, isBtc) ?? -Infinity) -
          (getCoinChange(a, isBtc) ?? -Infinity)
      );
    else sorted.sort((a, b) => getCoinValue(b, isBtc) - getCoinValue(a, isBtc));
    return sorted;
  }, [found, sortKey, isBtc]);

  const items: ListItem[] =
    loading && !rows.length
      ? [0, 1, 2, 3].map((index) => ({
          key: `skeleton-${index}`,
          skeleton: true,
        }))
      : rows;
  const pending = !!loading && !allCoins.length;
  const showHero = pending || allCoins.length > 0;

  const emptyState =
    !rows.length && !loading ? (
      <EmptyView
        iconKey={EmptyStatesType.Coins}
        title={t("assets.empty_title")}
        iconWidth={100}
        titleStyle={{ maxWidth: 300 }}
        objectName="coins"
        isSearch={!!search.value?.length}
        filtersSlice={portfolioFilters}
      />
    ) : undefined;

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: "value", label: t("phone.sort_value") },
    { key: "name", label: t("phone.sort_name") },
    { key: "change", label: t("phone.sort_change") },
    { key: "price", label: t("phone.sort_price") },
  ];

  const renderTop = () => (
    <>
      {showHero && (
        <PhoneAllocationCard
          label={t("phone.total_assets")}
          value={phoneMoney(summary.total, symbol, precision)}
          loading={pending}
          meta={`${phoneMoney(summary.available, symbol, precision)} ${t(
            "phone.available"
          ).toLowerCase()}`}
          segments={toSplitSegments(summary.shares)}
        />
      )}
      {(pending || rows.length > 0) && (
        <PhoneSectionHead
          title={t("tabs.assets")}
          extra={!pending && <Count>{rows.length}</Count>}
          trailing={
            <Trailing>
              <PhoneSortMenu
                options={sortOptions}
                value={sortKey}
                onChange={setSortKey}
                label={t("phone.sort_label")}
              />
              {onBuyClick && (
                <PhoneSectionAction type="button" onClick={onBuyClick}>
                  {t("buttons.buy")}
                </PhoneSectionAction>
              )}
            </Trailing>
          }
        />
      )}
    </>
  );

  return (
    <FlatList
      data={items}
      renderTopContainer={renderTop}
      renderContent={({ item, index }) =>
        isSkeleton(item) ? (
          <SkeletonRow pos={getPhoneSegment(index, items.length)} />
        ) : (
          <CoinRow
            coin={item}
            pos={getPhoneSegment(index, items.length)}
            precisionSelector={precisionSelector}
            isBtc={isBtc}
            symbol={symbol}
            tokens={tokens}
          />
        )
      }
      gap={0}
      paddingHorizontal={0}
      wrapperStyle={WRAPPER_STYLE}
      refreshing={refreshing}
      onRefresh={onRefresh}
      emptyState={emptyState}
    />
  );
};

export default PhoneAssets;

const WRAPPER_STYLE = { padding: "0 0 8px" };

interface CoinRowProps {
  coin: ConsolidatedCoinDataWithKey;
  pos: ReturnType<typeof getPhoneSegment>;
  precisionSelector: (state: RootState) => PrecisionConfig | undefined;
  isBtc: boolean;
  symbol: string;
  tokens: ReturnType<typeof getNewLookTokens>;
}

const CoinRow = React.memo(
  ({ coin, pos, precisionSelector, isBtc, symbol, tokens }: CoinRowProps) => {
    const { openCoinExtraDetails, getQuantity } = useAssetItem({
      precisionSelector,
    });
    const { getAsset, getExchange, getInitialPrice } = useCoinCard({});
    const { getDisplayCurrencyAmountPrecision } = usePrecisions();
    const isSubUser = useIsSubUser();
    const user = useSelectUser();
    const { graphData } = useGraphDataSocket(
      getAsset(coin) || "",
      getExchange(coin) || ""
    );
    const data = graphData ?? coin.graph_data;
    const index = isBtc ? 2 : 1;
    const { values, change } = useMemo(() => {
      const points = (data?.[TIME_RANGE_1_DAY] as number[][] | undefined) || [];
      const raw = Array.isArray(data?.change)
        ? data?.change[index - 1]
        : data?.change;
      return {
        values: points.map((point) => Number(point[index])),
        change:
          points.length > 1
            ? round(((raw as number) || 0) * 100, 2)
            : undefined,
      };
    }, [data, index]);
    const color =
      change === undefined
        ? tokens.muted
        : change
        ? directionColor(tokens, change)
        : tokens.muted;
    const open = () => openCoinExtraDetails(coin);
    const value = (isBtc ? coin.total_in_btc : coin.total_in_usd) || 0;
    const precision = getDisplayCurrencyAmountPrecision();

    return (
      <PhoneSegmentCard $pos={pos}>
        <PhoneSegmentRow $divider={pos === "first" || pos === "middle"}>
          <Row
            role="button"
            tabIndex={0}
            data-testid={PHONE_ASSET_ROW_ID}
            aria-label={coin.symbol}
            onClick={open}
            onKeyDown={(event) => {
              if (event.key === "Enter" && event.target === event.currentTarget)
                open();
            }}
          >
            <IconWithFallback
              iconType={ICON_TYPES.COIN}
              coinOrExchangeName={coin.symbol}
              pxIconSize={34}
            />
            <Names>
              <PhoneName>{coin.symbol}</PhoneName>
              <PhoneMeta>
                <AutoSizeText
                  inheritFont={true}
                  minFontSizePx={FIGURE_MIN_FONT_SIZE}
                >
                  {symbol}
                  <LiveSymbolPrice
                    symbol={getAsset(coin) || ""}
                    exchange={getExchange(coin) || ""}
                    initialPrice={getInitialPrice(coin) || 0}
                    hideIcon={true}
                    colorValueChange={false}
                    fontSize="inherit"
                    textColor="inherit"
                    style={INLINE_STYLE}
                  />
                </AutoSizeText>
              </PhoneMeta>
            </Names>
            <Trend>
              {values.length > 1 && (
                <div data-testid={PHONE_ASSET_SPARK_ID}>
                  <NewLookSparkline
                    values={values}
                    color={color}
                    width={SPARK_WIDTH}
                    height={20}
                    lineOnly={true}
                  />
                </div>
              )}
              {change !== undefined && (
                <TrendChange style={{ color }}>
                  {phonePercent(change)}
                </TrendChange>
              )}
            </Trend>
            <Amounts>
              <Value>
                <AutoSizeText
                  inheritFont={true}
                  minFontSizePx={FIGURE_MIN_FONT_SIZE}
                >
                  {phoneMoney(value, symbol, precision)}
                </AutoSizeText>
              </Value>
              <PhoneSub style={{ color: tokens.muted }}>
                <AutoSizeText
                  inheritFont={true}
                  minFontSizePx={FIGURE_MIN_FONT_SIZE}
                >
                  {getQuantity(coin.quantity, coin.symbol)}
                </AutoSizeText>
              </PhoneSub>
            </Amounts>
            {!isSubUser && !user?.isWhopExperience() ? (
              <Actions onClick={(event) => event.stopPropagation()}>
                <CoinActionsDropdown coinData={coin} />
              </Actions>
            ) : (
              <PhoneActions style={{ width: 0 }} />
            )}
          </Row>
        </PhoneSegmentRow>
      </PhoneSegmentCard>
    );
  }
);

const SkeletonRow = ({ pos }: { pos: ReturnType<typeof getPhoneSegment> }) => (
  <PhoneSegmentCard $pos={pos} aria-hidden={true}>
    <PhoneSegmentRow $divider={pos === "first" || pos === "middle"}>
      <PhoneRow as="div" style={{ cursor: "default" }}>
        <PhoneSkeleton $width={34} $height={34} $radius={17} />
        <PhoneNames>
          <PhoneSkeleton $width={80} $height={12} />
          <PhoneSkeleton $width={110} $height={10} style={{ marginTop: 6 }} />
        </PhoneNames>
        <PhoneSkeleton $width={64} $height={14} />
      </PhoneRow>
    </PhoneSegmentRow>
  </PhoneSegmentCard>
);

const Count = styled.span`
  font-size: 13px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Row = styled(PhoneRow)`
  @media (max-width: 374px) {
    gap: 8px;
  }
`;
const Names = styled(PhoneNames)`
  min-width: 58px;
`;
const Trend = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: ${SPARK_WIDTH}px;
`;
const Amounts = styled.div`
  flex: 0 1 auto;
  min-width: 5.7em;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: right;
  font-size: 14px;
  @media (max-width: 374px) {
    font-size: 13px;
  }
`;
const Value = styled.div`
  font-size: inherit;
  line-height: 20px;
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Actions = styled(PhoneActions)`
  margin: 0 -12px 0 -14px;
  @media (max-width: 374px) {
    margin-left: -12px;
  }
`;
const TrendChange = styled.span`
  ${newLookPhoneType.meta};
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
const Trailing = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
